import React, { lazy, Suspense, useContext } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { enumsRolesPermissions, enumsTypeUser } from "utils/enums";
import { loaderElement } from "utils/loaderElement";
import Context from "contextAPI/UserContext";
import ProtectedRoute from "routes/ProtectedRoute";
import useAxiosInterceptor from "utils/axiosInterceptor";
import BpmnModels from "components/pages/BpmnModels";

const Login = lazy(() => import("components/pages/Login"));
const RecoveryPassword = lazy(() => import("components/pages/RecoveryPassword"));
const ChangePassword = lazy(() => import("components/pages/ChangePassword"));
const ChangePasswordExpiration = lazy(() => import("components/pages/ChangePasswordExpiration"));
const ChangeForgottenPasswordReset = lazy(
  () => import("components/pages/ChangeForgottenPasswordReset"),
);
const ProfilePage = lazy(() => import("components/pages/ProfilePage"));
const ProfileRubricPage = lazy(() => import("components/pages/ProfileRubricPage"));
const Dashboard = lazy(() => import("components/pages/Dashboard"));
const Metrics = lazy(() => import("components/pages/Metrics"));
const MetricsProcessByMonth = lazy(() => import("components/pages/MetricsProcessByMonth"));
const MetricsRecordsByForm = lazy(() => import("components/pages/MetricsRecordsByForm"));
const MetricsRecordByYear = lazy(() => import("components/pages/MetricsRecordsByYear"));
const UserList = lazy(() => import("components/pages/UserList"));
const CreateUser = lazy(() => import("components/pages/CreateUser"));
const Trd = lazy(() => import("components/pages/Trd"));
const CreateList = lazy(() => import("components/pages/CreateList"));
const UserRoles = lazy(() => import("components/pages/UserRoles"));
const WorkGroup = lazy(() => import("components/pages/UserWorkGroup"));
const UserWorkGroupForm = lazy(() => import("components/pages/UserWorkgroupForm"));
const UserWorkGroupMembers = lazy(() => import("components/pages/UserWorkGroupMember"));
const WorkFlow = lazy(() => import("components/pages/WorkFlow"));
const WorkFlowList = lazy(() => import("components/pages/WorkFlowList"));
const UserRolesForm = lazy(() => import("components/pages/UserRolesForm"));
const UserRolesMembers = lazy(() => import("components/pages/UserRolesMembers"));
const UserRolesConfig = lazy(() => import("components/pages/UserRolesConfig"));
const ConsultList = lazy(() => import("components/pages/ConsultList"));
const ItemsList = lazy(() => import("components/pages/ItemsList"));
const TrdExpedients = lazy(() => import("components/pages/TrdExpedients"));
const NotFound = lazy(() => import("components/pages/NotFound"));
const FormsDesigner = lazy(() => import("components/pages/FormsDesigner/FormsDesigner"));
const FormsList = lazy(() => import("components/pages/FormsList"));
const FieldsDesigner = lazy(() => import("components/pages/FieldDesigner"));
const RecordData = lazy(() => import("components/pages/RecordData"));
const RecordFields = lazy(() => import("components/pages/RecordFields"));
const RecordDetail = lazy(() => import("components/pages/RecordDetail"));
const RecordDetails = lazy(() => import("components/pages/RecordDetails"));
const RecordList = lazy(() => import("components/pages/RecordList"));
const RecordListByRecord = lazy(() => import("components/pages/RecordListBYRecord"));
const RecordListByForm = lazy(() => import("components/pages/RecordListByForm"));
const EditList = lazy(() => import("components/pages/EditList"));
const EditItemList = lazy(() => import("components/pages/EditItemList"));
const TrdOrdinationUnits = lazy(() => import("components/pages/TrdOrdinationUnits"));
const EditRecord = lazy(() => import("components/pages/EditRecord"));
const EditExpedients = lazy(() => import("components/pages/EditExpedients"));
const TaskBox = lazy(() => import("components/pages/TaskBox"));
const ProcessDetail = lazy(() => import("components/pages/ProcessDetail"));
const WorkFlowEdit = lazy(() => import("components/pages/WorkFlowEdit"));
const WorkFlowSteps = lazy(() => import("components/pages/WorkFlowSteps"));
const WorkFlowConfigurationSteps = lazy(
  () => import("components/pages/WorkFlowConfigurationSteps"),
);
const WorkflowView = lazy(() => import("components/pages/WorkflowView"));
const Integrations = lazy(() => import("components/pages/Integrations"));
const DetailIntegrations = lazy(() => import("components/pages/DetailIntegrations"));
const RequestIntegration = lazy(() => import("components/pages/RequestIntegration"));
const ElectronicSignature = lazy(() => import("components/pages/ElectronicSignature"));
const ReportElectronicSignatures = lazy(
  () => import("components/pages/ReportElectronicSignatures"),
);
const Signatories = lazy(() => import("components/pages/Signatories"));
const InvoicesIntegrationConfigCreate = lazy(
  () => import("components/pages/InvoicesIntegrationConfigCreate"),
);
const InvoicesIntegrationConfigEdit = lazy(
  () => import("components/pages/InvoicesIntegrationConfigEdit"),
);
const WorkflowsTraceability = lazy(() => import("components/pages/WorkflowsTraceability"));
const FilterForm = lazy(() => import("components/pages/FilterForm"));
const ConsultConfigurationFields = lazy(
  () => import("components/pages/ConsultConfigurationFields"),
);
const ChangeOrderFields = lazy(() => import("components/pages/ChangeOrderFields"));
const ChangeUserStatus = lazy(() => import("components/pages/ChangeUserStatus"));
const DocumentaryTypes = lazy(() => import("components/pages/DocumentaryTypes"));
const UserLog = lazy(() => import("components/pages/UserLog"));
const TrdExpedientsConsultation = lazy(() => import("components/pages/TrdExpedientsConsultation"));
const DocumentaryTypesEdition = lazy(() => import("components/pages/DocumentaryTypesEdition"));
const NotFoundFile = lazy(() => import("components/pages/NotFoundFile/NotFoundFile"));
const Parameters = lazy(() => import("components/pages/Parameters"));
const ProcessManagement = lazy(
  () => import("components/pages/ProcessManagement/ProcessManagement"),
);
const TrashConsult = lazy(() => import("components/pages/TrashConsult"));
const AuditLogs = lazy(() => import("components/pages/AuditLogs"));
const ExportData = lazy(() => import("components/pages/ExportData/ExportData"));
const CreateUserApi = lazy(() => import("components/pages/CreateUserApi"));
const CreateUserApiPageForm = lazy(() => import("components/pages/CreateUserApiPageForm"));
const PDFViewer = lazy(() => import("components/pages/PDFViewerPage"));
const DocumentTemplatesCreatePage = lazy(
  () => import("components/pages/DocumentTemplatesCreatePage"),
);
const DocumentTemplatesConsult = lazy(() => import("components/pages/DocumentTemplatesConsult"));
const ProcessCompleteConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));
const ProcessInCourseConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));
const ProcessInterruptedConsult = lazy(() => import("components/pages/ProcessMetricsConsult"));

const App = () => {
  const {
    userType,
    rolesPermissionsGranted,
    designerForms,
    designerFormsPermissions,
    workflowAllForms,
    workflowFormsPermissions,
    jwt,
    sessionReady,
  } = useContext(Context);

  // CONTROL AND PERMISSIONS
  const isPasswordExpired = Boolean(jwt) && sessionReady === false;
  const isLogged = Boolean(jwt) && sessionReady === true;

  useAxiosInterceptor();

  let access;
  let accessTRD;
  let accessDesignerForms;
  let accessDesignerFormsConsult;
  let accessWorkflowsForms;
  let accessWorkflowsFormsConsult;
  let accessList;
  let accessUserManager;
  let accessRoles;
  let accessIntegrations;
  let accessWorkGroups;
  let accessProcessManagement;
  let accessMetrics;
  let accessAuditLogs;
  let accessTemplateDesigner;

  /**
   * Checks if the user is logged in and has a user type other than QUERY.
   * Sets access to true if conditions are met, false otherwise.
   */
  if (isLogged === true && userType !== enumsTypeUser.QUERY) {
    access = true;
  } else {
    access = false;
  }

  /**
   * Checks the user's access to TRD based on their login status, user type, and role permissions.
   * If the user is logged in as a standard user with specific role permissions or as a query user,
   * sets the access to TRD to true; otherwise, sets it to false.
   */
  if (
    (isLogged === true &&
      userType === enumsTypeUser.STANDARD &&
      rolesPermissionsGranted !== null &&
      rolesPermissionsGranted.includes(enumsRolesPermissions.TRD_CONSFIGURATION)) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessTRD = true;
  } else {
    accessTRD = false;
  }

  /**
   * Checks the user's access to designer forms based on their login status, user type, and designer form permissions.
   * If the user is logged in, has a standard user type, and has designer form permissions, or if the user is logged in as an admin,
   * sets the access to designer forms to true; otherwise, sets it to false.
   */
  if (
    (isLogged === true && userType === enumsTypeUser.STANDARD && designerForms === true) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessDesignerForms = true;
  } else {
    accessDesignerForms = false;
  }

  /**
   * Checks if the user is logged in, has a standard user type, and has permission for designer forms.
   * Sets accessDesignerFormsConsult to true if conditions are met, false otherwise.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    designerFormsPermissions.length !== 0
  ) {
    accessDesignerFormsConsult = true;
  } else {
    accessDesignerFormsConsult = false;
  }

  /**
   * Checks the user's access to the workflows forms based on their login status, user type, and permissions.
   * Sets the accessWorkflowsForms variable to true if the user is logged in as a standard user with access to all workflow forms or as an admin; otherwise, sets it to false.
   */
  if (
    (isLogged === true && userType === enumsTypeUser.STANDARD && workflowAllForms === true) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessWorkflowsForms = true;
  } else {
    accessWorkflowsForms = false;
  }

  /**
   * Checks if the user is logged in, has a standard user type, and has permission to access workflow forms.
   * Sets the accessWorkflowsFormsConsult variable accordingly.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    workflowFormsPermissions.length !== 0
  ) {
    accessWorkflowsFormsConsult = true;
  } else {
    accessWorkflowsFormsConsult = false;
  }

  /**
   * Checks the user's access rights based on their login status, user type, and role permissions.
   * If the user is logged in as a standard user with specific role permissions or as an admin,
   * sets the accessList flag to true; otherwise, sets it to false.
   */
  if (
    (isLogged === true &&
      userType === enumsTypeUser.STANDARD &&
      rolesPermissionsGranted !== null &&
      rolesPermissionsGranted.includes(enumsRolesPermissions.LIST_DESIGNER)) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessList = true;
  } else {
    accessList = false;
  }

  /**
   * Checks the user's access rights to the user manager based on their login status, user type, and role permissions.
   * If the user is logged in as a standard user with specific role permissions or as an admin, grants access to the user manager.
   * @returns {boolean} - True if the user has access to the user manager, false otherwise.
   */
  if (
    (isLogged === true &&
      userType === enumsTypeUser.STANDARD &&
      rolesPermissionsGranted !== null &&
      rolesPermissionsGranted.includes(enumsRolesPermissions.USER_MANAGER)) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessUserManager = true;
  } else {
    accessUserManager = false;
  }

  /**
   * Checks the user's access roles based on login status, user type, and role permissions.
   * If the user is logged in, has a standard user type, and specific role permissions, sets accessRoles to true.
   * If the user is logged in and has an admin user type, sets accessRoles to true.
   * Otherwise, sets accessRoles to false.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    rolesPermissionsGranted !== null &&
    rolesPermissionsGranted.includes(enumsRolesPermissions.ROLE_MANAGER)
  ) {
    accessRoles = true;
  } else if (isLogged === true && userType === enumsTypeUser.ADMIN) {
    accessRoles = true;
  } else {
    accessRoles = false;
  }

  /**
   * Determines the access to integrations based on the user's login status and user type.
   * If the user is logged in and not an admin, access to integrations is denied; otherwise, access is granted.
   */
  if (isLogged === true && userType !== enumsTypeUser.ADMIN) {
    accessIntegrations = false;
  } else {
    accessIntegrations = true;
  }

  /**
   * Determines if the user has access to work groups based on their login status, user type, and role permissions.
   * @param {boolean} isLogged - Indicates if the user is logged in.
   * @param {string} userType - The type of user (QUERY, STANDARD, ADMIN).
   * @param {array} rolesPermissionsGranted - The array of role permissions.
   * @returns {boolean} - True if the user has access to work groups, false otherwise.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    rolesPermissionsGranted !== null &&
    rolesPermissionsGranted.includes(enumsRolesPermissions.WORK_GROUP_MANAGER)
  ) {
    accessWorkGroups = true;
  } else if (isLogged === true && userType === enumsTypeUser.ADMIN) {
    accessWorkGroups = true;
  } else {
    accessWorkGroups = false;
  }

  /**
   * Determines if the user has access to process management based on their login status, user type, and role permissions.
   * @param {boolean} isLogged - Indicates if the user is logged in.
   * @param {string} userType - The type of user (QUERY, STANDARD, ADMIN).
   * @param {array} rolesPermissionsGranted - The array of role permissions.
   * @returns {boolean} - True if the user has access to process management, false otherwise.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    rolesPermissionsGranted !== null &&
    rolesPermissionsGranted.includes(enumsRolesPermissions.PROCESS_MANAGEMENT)
  ) {
    accessProcessManagement = true;
  } else if (isLogged === true && userType === enumsTypeUser.ADMIN) {
    accessProcessManagement = true;
  } else {
    accessProcessManagement = false;
  }

  /**
   * Determines if the user has access to metrics based on their login status, user type, and role permissions.
   * @param {boolean} isLogged - Indicates if the user is logged in.
   * @param {string} userType - The type of user (QUERY, STANDARD, ADMIN).
   * @param {array} rolesPermissionsGranted - The array of role permissions.
   * @returns {boolean} - True if the user has access to metrics, false otherwise.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    rolesPermissionsGranted !== null &&
    rolesPermissionsGranted.includes(enumsRolesPermissions.METRICS)
  ) {
    accessMetrics = true;
  } else if (isLogged === true && userType === enumsTypeUser.ADMIN) {
    accessMetrics = true;
  } else {
    accessMetrics = false;
  }

  /**
   * Determines if the user has access to audit logs based on their login status, user type, and role permissions.
   * @param {boolean} isLogged - Indicates if the user is logged in.
   * @param {string} userType - The type of user (QUERY, STANDARD, ADMIN).
   * @param {array} rolesPermissionsGranted - The array of role permissions.
   * @returns {boolean} - True if the user has access to audit logs, false otherwise.
   */
  if (
    isLogged === true &&
    userType === enumsTypeUser.STANDARD &&
    rolesPermissionsGranted !== null &&
    rolesPermissionsGranted.includes(enumsRolesPermissions.AUDIT_LOG)
  ) {
    accessAuditLogs = true;
  } else if (isLogged === true && userType === enumsTypeUser.ADMIN) {
    accessAuditLogs = true;
  } else {
    accessAuditLogs = false;
  }

  /**
   * Determines whether the current user can access the template designer.
   * Sets `accessTemplateDesigner` to true if the user is logged in and either:
   * - has user type `STANDARD` with the `TEMPLATE_DESIGNER` permission, or
   * - has user type `ADMIN`.
   * Otherwise, sets it to false.
   * Depends on: `isLogged`, `userType`, `rolesPermissionsGranted`,
   * `enumsTypeUser`, and `enumsRolesPermissions`.
   */
  if (
    (isLogged === true &&
      userType === enumsTypeUser.STANDARD &&
      rolesPermissionsGranted !== null &&
      rolesPermissionsGranted.includes(enumsRolesPermissions.TEMPLATE_DESIGNER)) ||
    (isLogged === true && userType === enumsTypeUser.ADMIN)
  ) {
    accessTemplateDesigner = true;
  } else {
    accessTemplateDesigner = false;
  }

  return (
    <Router>
      <Suspense fallback={<div className="content-centered">{loaderElement(true)}</div>}>
        <Switch>
          {/*------------ URL'S LOGUIN, PASSWORD */}

          <ProtectedRoute exact path="/" component={Login} accessCondition={isLogged === false} />
          <ProtectedRoute
            exact
            path="/user/recoverpassword"
            component={RecoveryPassword}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/nueva-contrasena"
            component={ChangePassword}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/restablecimiento-de-contrasena"
            component={ChangeForgottenPasswordReset}
            accessCondition={isLogged === false}
          />
          <ProtectedRoute
            exact
            path="/renew-password"
            component={ChangePasswordExpiration}
            accessCondition={jwt && (isPasswordExpired || isLogged)}
          />

          <ProtectedRoute
            exact
            path="/profile"
            component={ProfilePage}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/profile/rubric"
            component={ProfileRubricPage}
            accessCondition={isLogged === true}
          />

          {/*------------ URL'S EXPORT DATA */}

          <ProtectedRoute
            exact
            path="/export-data"
            component={ExportData}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/export-data/process"
            component={ExportData}
            accessCondition={isLogged === true}
          />

          {/*------------ URL'S ACCESS TRD */}

          <ProtectedRoute exact path="/trd" component={Trd} accessCondition={access && accessTRD} />
          <ProtectedRoute
            exact
            path="/trd/ordination/:id?/:backgroundId?"
            component={TrdOrdinationUnits}
            accessCondition={access && accessTRD}
          />
          <ProtectedRoute
            exact
            path="/trd/expedient/:id?/:backgroundId?"
            component={TrdExpedients}
            accessCondition={access && accessTRD}
          />
          <ProtectedRoute
            exact
            path="/application/config/parameters"
            component={Parameters}
            accessCondition={access === true}
          />
          <ProtectedRoute
            exact
            path="/trd/expedients/edit/:expedientId"
            component={EditExpedients}
            accessCondition={access === true && accessTRD === true}
          />
          <ProtectedRoute
            exact
            path="/documentary-types/list"
            component={DocumentaryTypes}
            accessCondition={access === true}
          />
          <ProtectedRoute
            exact
            path="/documentary-types/list/:documentaryId/:documentaryName"
            component={DocumentaryTypesEdition}
            accessCondition={access === true}
          />

          {/*------------- URL'S DASHBOARD */}

          <ProtectedRoute
            exact
            path="/dashboard"
            component={Dashboard}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S RECORD LIST FORMS */}

          <ProtectedRoute
            exact
            path="/record/list/forms"
            component={RecordData}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/create/:id"
            component={RecordFields}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/details/:recordId/:id"
            component={RecordDetails}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/details/:recordId/:id/:fileId"
            component={RecordDetails}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/edit/:id/:recordId"
            component={EditRecord}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/edit/:id/:recordId/:query"
            component={EditRecord}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/traceability/record/:id/:recordId"
            component={WorkflowsTraceability}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S RECORD CONSULT LIST FORMS */}

          <ProtectedRoute
            exact
            path="/record/list/form"
            component={RecordListByForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/table/:id"
            component={RecordList}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/forms/filters/:id"
            component={FilterForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/list/forms/filters/:id/:query"
            component={FilterForm}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id/:fileId"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/record/detail/:recordId/:id"
            component={RecordDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/trd/expedients/consultation"
            component={TrdExpedientsConsultation}
            accessCondition={isLogged === true}
          />

          <ProtectedRoute
            exact
            path="/pdf-viewer"
            component={PDFViewer}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S CONSULT BY RECORD */}

          <ProtectedRoute
            exact
            path="/record/list/record"
            component={RecordListByRecord}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S UPLOAD FILES AND SIGNATORIES */}

          <ProtectedRoute
            exact
            path="/files/signature/:formId/:hash"
            component={ElectronicSignature}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/signatories/:id"
            component={Signatories}
            accessCondition={isLogged === true}
          />

          {/*----------- URL'S DEATILRECORDPROCESS */}

          <ProtectedRoute
            exact
            path="/traceability/workflow/:id/:recordId"
            component={WorkflowsTraceability}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/process/detail/:id/:processId"
            component={ProcessDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/process/management"
            component={ProcessManagement}
            accessCondition={isLogged === true && access && accessProcessManagement}
          />
          <ProtectedRoute
            exact
            path="/taskBox/detail/:id"
            component={ProcessDetail}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/taskBox"
            component={TaskBox}
            accessCondition={isLogged === true}
          />
          <ProtectedRoute
            exact
            path="/taskBox/:param"
            component={TaskBox}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S REPORT SIGNATURES */}

          <ProtectedRoute
            exact
            path="/reports/signatures"
            component={ReportElectronicSignatures}
            accessCondition={isLogged === true}
          />

          {/*--------- URL'S TRASH BIN */}

          <ProtectedRoute
            exact
            path="/trash"
            component={TrashConsult}
            accessCondition={isLogged === true}
          />

          {/*------------- URL'S FORMSDESIGNER */}

          <ProtectedRoute
            exact
            path="/formsdesigner/create"
            component={FormsDesigner}
            accessCondition={accessDesignerForms && !accessDesignerFormsConsult}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/list"
            component={FormsList}
            accessCondition={
              (access && accessDesignerForms) || (access && accessDesignerFormsConsult)
            }
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/edit/:id"
            component={FormsDesigner}
            accessCondition={access}
          />

          {/*----------- URL'S FORMSDESIGNER FIELDS */}

          <ProtectedRoute
            exact
            path="/formsdesigner/fields/:id/"
            component={FieldsDesigner}
            accessCondition={access === true}
          />
          <ProtectedRoute
            exact
            path="/configuration/edit/:id/:form_uuid/:hashRecord/"
            component={ConsultConfigurationFields}
            accessCondition={access === true}
          />
          <ProtectedRoute
            exact
            path="/configuration/order/:id/:hash/:formId"
            component={ChangeOrderFields}
            accessCondition={access === true}
          />
          <ProtectedRoute
            exact
            path="/configuration/consult/:id/:form_uuid/:status/:hashRecord"
            component={ConsultConfigurationFields}
            accessCondition={access === true}
          />

          {/*------------ URL'S CREATELIST */}

          <ProtectedRoute
            exact
            path="/formsdesigner/createlist"
            component={CreateList}
            accessCondition={access && accessList}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/consultlist"
            component={ConsultList}
            accessCondition={access && accessList}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/editlist/:id"
            component={EditList}
            accessCondition={access && accessList}
          />

          {/*----------- URL'S ITEMSlIST */}

          <ProtectedRoute
            exact
            path="/formsdesigner/itemslist/:id/:parentId?"
            component={ItemsList}
            accessCondition={access && accessList}
          />
          <ProtectedRoute
            exact
            path="/formsdesigner/editItem/list/:listId/itemslist/:id"
            component={EditItemList}
            accessCondition={access && accessList}
          />

          {/*----------- URL'S ADMINUSERS */}

          <ProtectedRoute
            exact
            path="/user/create"
            component={CreateUser}
            accessCondition={access && accessUserManager}
          />
          <ProtectedRoute
            exact
            path="/user/list"
            component={UserList}
            accessCondition={access && accessUserManager}
          />
          <ProtectedRoute
            exact
            path="/user/edit/:id"
            component={CreateUser}
            accessCondition={access && accessUserManager}
          />
          <ProtectedRoute
            exact
            path="/user/edit/status/:id"
            component={ChangeUserStatus}
            accessCondition={access && accessUserManager}
          />
          <ProtectedRoute
            exact
            path="/user/log/:id"
            component={UserLog}
            accessCondition={accessUserManager}
          />
          <ProtectedRoute
            exact
            path="/user/workgroup"
            component={WorkGroup}
            accessCondition={access === true && accessWorkGroups === true}
          />
          <ProtectedRoute
            exact
            path="/user/create/workgroup"
            component={UserWorkGroupForm}
            accessCondition={access === true && accessWorkGroups === true}
          />
          <ProtectedRoute
            exact
            path="/user/workgroup/members/:id/:statusGroup"
            component={UserWorkGroupMembers}
            accessCondition={access === true && accessWorkGroups === true}
          />
          <ProtectedRoute
            exact
            path="/user/create/workgroup/:id"
            component={UserWorkGroupForm}
            accessCondition={access === true && accessWorkGroups === true}
          />
          <ProtectedRoute
            exact
            path="/user/Third-party-accounts"
            component={CreateUserApi}
            accessCondition={access}
          />
          <ProtectedRoute
            exact
            path="/user/Third-party-accounts/create"
            component={CreateUserApiPageForm}
            accessCondition={access}
          />

          {/*---------- URL'S ROLES */}

          <ProtectedRoute
            exact
            path="/user/roles"
            component={UserRoles}
            accessCondition={access && accessRoles}
          />
          <ProtectedRoute
            exact
            path="/user/create/roles"
            component={UserRolesForm}
            accessCondition={access && accessRoles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/edit/:id"
            component={UserRolesForm}
            accessCondition={access && accessRoles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/members/:id"
            component={UserRolesMembers}
            accessCondition={access && accessRoles}
          />
          <ProtectedRoute
            exact
            path="/user/roles/config/:id/:hash"
            component={UserRolesConfig}
            accessCondition={access && accessRoles}
          />

          {/*--------- URL'S METRICS */}

          <ProtectedRoute
            exact
            path="/metrics"
            component={Metrics}
            accessCondition={access && accessMetrics}
          />
          <ProtectedRoute
            exact
            path="/metrics/recordListbyForm"
            component={MetricsRecordsByForm}
            accessCondition={access}
          />
          <ProtectedRoute
            exact
            path="/audit/logs"
            component={AuditLogs}
            accessCondition={access && accessAuditLogs}
          />
          <ProtectedRoute
            exact
            path="/metrics/recordListByYear"
            component={MetricsRecordByYear}
            accessCondition={access}
          />
          <ProtectedRoute
            exact
            path="/metrics/proccessbymonth"
            component={MetricsProcessByMonth}
            accessCondition={access}
          />

          {/*------------- URL'S WORKFLOWDESIGNER */}

          <ProtectedRoute
            exact
            path="/workflow"
            component={WorkFlow}
            accessCondition={access && accessWorkflowsForms && !accessWorkflowsFormsConsult}
          />
          <ProtectedRoute
            exact
            path="/workflow/designer/:id"
            component={BpmnModels}
            accessCondition={access && accessWorkflowsForms && !accessWorkflowsFormsConsult}
          />
          <ProtectedRoute
            exact
            path="/workflow/list"
            component={WorkFlowList}
            accessCondition={
              (access && accessWorkflowsForms) || (access && accessWorkflowsFormsConsult)
            }
          />
          <ProtectedRoute
            exact
            path="/workflow/edit/:id"
            component={WorkFlowEdit}
            accessCondition={access}
          />
          <ProtectedRoute
            exact
            path="/workflow/steps/:id/:hash/:formId"
            component={WorkFlowSteps}
            accessCondition={access}
          />
          <ProtectedRoute
            exact
            path="/workflow/view/:model"
            component={WorkflowView}
            accessCondition={access}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-complete/:workflow_name/:workflow_id"
            component={ProcessCompleteConsult}
            accessCondition={access}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-inProgress/:workflow_name/:workflow_id"
            component={ProcessInCourseConsult}
            accessCondition={access}
          />

          <ProtectedRoute
            exact
            path="/metrics/process-interrupted/:workflow_name/:workflow_id"
            component={ProcessInterruptedConsult}
            accessCondition={access}
          />

          {/*------------- URL'S TEMPLATES DESIGNER*/}

          <ProtectedRoute
            exact
            path="/templates/create"
            component={DocumentTemplatesCreatePage}
            accessCondition={access && accessTemplateDesigner}
          />

          <ProtectedRoute
            exact
            path="/templates/list"
            component={DocumentTemplatesConsult}
            accessCondition={access && accessTemplateDesigner}
          />

          <ProtectedRoute
            exact
            path="/templates/edit/:idTemplate"
            component={DocumentTemplatesCreatePage}
            accessCondition={access && accessTemplateDesigner}
          />

          {/*------------- URL'S WORKFLOWDESIGNER STEPS */}

          <ProtectedRoute
            exact
            path="/workflow/steps/configuration/:id/:workflowid/:hash/:formId"
            component={WorkFlowConfigurationSteps}
            accessCondition={access}
          />

          {/*--------- URL'S INTEGRATIONS */}

          <ProtectedRoute
            exact
            path="/integrations"
            component={Integrations}
            accessCondition={access && accessIntegrations}
          />
          <ProtectedRoute
            exact
            path="/integrations/:id"
            component={DetailIntegrations}
            accessCondition={access && accessIntegrations}
          />
          <ProtectedRoute
            exact
            path="/integrations/request/:id"
            component={RequestIntegration}
            accessCondition={access && accessIntegrations}
          />

          <ProtectedRoute
            exact
            path="/integrations/:id/create-config/invoices"
            component={InvoicesIntegrationConfigCreate}
            accessCondition={access && accessIntegrations}
          />

          <ProtectedRoute
            exact
            path="/integrations/:id/edit-config/invoices"
            component={InvoicesIntegrationConfigEdit}
            accessCondition={access && accessIntegrations}
          />

          {/*---------- URL NOTFOUND */}

          <ProtectedRoute
            exact
            path="/file-not-found"
            component={NotFoundFile}
            accessCondition={isLogged === true}
          />
          <Route exact path="*" component={NotFound} />
        </Switch>
      </Suspense>
    </Router>
  );
};

export default App;
